
"use client";

import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { fireDB } from "../../../Backend/Firebase/FirebaseConfigData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";

function CreateBookPage() {
  const navigate = useNavigate();

  // ✅ MUST MATCH THE ROUTE: /createLibrary/:librarySlug/create-book
  const { librarySlug } = useParams();

  const [bookData, setBookData] = useState({
    name: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    canonicalUrl: "",
  });

  const [isBookCreated, setIsBookCreated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Track manual slug edits
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  // Auto-generate slug from title
  useEffect(() => {
    if (bookData.name && !isSlugEdited) {
      const generatedSlug = bookData.name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "_");

      setBookData((prev) => ({
        ...prev,
        slug: generatedSlug,
      }));
    }
  }, [bookData.name, isSlugEdited]);

  // Auto-generate canonical URL
  useEffect(() => {
    if (bookData.slug) {
      setBookData((prev) => ({
        ...prev,
        canonicalUrl: `https://greatmastersai.com/${prev.slug}`,
      }));
    }
  }, [bookData.slug]);

  // -------------------------------------------------------------
  // ✅ CHECK IF SLUG EXISTS INSIDE SELECTED LIBRARY COLLECTION
  // -------------------------------------------------------------
  const checkSlugExists = async (slug: string) => {
    if (!librarySlug) return false; // prevents crash

    const bookRef = collection(fireDB, "NewBlogs", librarySlug, "books");
    const q = query(bookRef, where("slug", "==", slug));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  // -------------------------------------------------------------
  // ✅ CREATE BOOK UNDER:
  // NewBlogs / <librarySlug> / books / <slug>
  // -------------------------------------------------------------
  const createBook = async () => {
    if (!bookData.name || !bookData.slug) {
      toast.error("Book Title and Slug are required!", {
        position: "top-center",
      });
      return;
    }

    // Check for duplicate slug
    const exists = await checkSlugExists(bookData.slug);
    if (exists) {
      toast.error("This slug already exists in this library!", {
        position: "top-center",
      });
      return;
    }

    setIsLoading(true);

    try {
      const bookRef = doc(
        fireDB,
        "NewBlogs",
        librarySlug!,
        "books",
        bookData.slug
      );

      await setDoc(bookRef, {
        ...bookData,
        seo: {
          title: bookData.metaTitle || bookData.name,
          description: bookData.metaDescription,
          keywords: bookData.metaKeywords
            .split(",")
            .map((k: string) => k.trim()),
          canonicalUrl: bookData.canonicalUrl,
        },
        createdAt: new Date().toISOString(),
        time: Timestamp.now(),
      });

      toast.success("🎉 Book created successfully!", {
        position: "top-center",
      });

      setIsBookCreated(true);
    } catch (err: unknown) {
      console.error("❌ Error:", err);
      toast.error("❌ Error creating book", { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------------------------------------------------
  // ✅ GO TO CHAPTER PAGE
  // -------------------------------------------------------------
  const goToAddChapter = () => {
    if (!bookData.slug) {
      toast.error("Book slug missing!", { position: "top-center" });
      return;
    }

    navigate(
      `/createLibrary/${librarySlug}/create-book/${bookData.slug}/create-chapter`
    );
  };

  // -------------------------------------------------------------
  // UI STARTS HERE
  // -------------------------------------------------------------
  return (
    <div className="bg-gray-50 min-h-screen py-10 px-6">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* HEADER */}
        <h1 className="text-4xl font-bold mb-6 text-center text-gray-800">
          📚 Create New Book
        </h1>

        {/* BOOK INFO CARD */}
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 space-y-4">
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">
            Book Information
          </h2>

          {/* Title */}
          <div>
            <label className="block text-gray-800 font-medium mb-1">
              Book Title
            </label>
            <input
              placeholder="Enter book title"
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-green-400"
              value={bookData.name}
              onChange={(e) =>
                setBookData({ ...bookData, name: e.target.value })
              }
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-gray-800 font-medium mb-1">
              Slug ID (editable)
            </label>
            <input
              placeholder="Enter slug"
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-green-400"
              value={bookData.slug}
              onChange={(e) => {
                setBookData({ ...bookData, slug: e.target.value });
                setIsSlugEdited(true);
              }}
            />
            <p className="text-gray-500 text-sm mt-1">
              Auto-generated but editable (example: <b>my_first_book</b>)
            </p>
          </div>

          {/* Canonical URL */}
          <div>
            <label className="block text-gray-800 font-medium mb-1">
              Canonical URL (auto)
            </label>
            <input
              className="w-full border p-3 rounded-lg bg-gray-100 cursor-not-allowed"
              value={bookData.canonicalUrl}
              readOnly
            />
          </div>
        </div>

        {/* SEO SETTINGS */}
        <div className="bg-yellow-50 p-6 rounded-2xl shadow-lg border border-yellow-200 space-y-4">
          <h2 className="text-2xl font-semibold text-yellow-700 mb-4">
            SEO Settings
          </h2>

          {/* Meta Title */}
          <div>
            <label className="block text-yellow-800 font-medium mb-1">
              Meta Title
            </label>
            <input
              placeholder="Enter SEO meta title"
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-yellow-400"
              value={bookData.metaTitle}
              onChange={(e) =>
                setBookData({ ...bookData, metaTitle: e.target.value })
              }
            />
          </div>

          {/* Meta Description */}
          <div>
            <label className="block text-yellow-800 font-medium mb-1">
              Meta Description
            </label>
            <textarea
              placeholder="Enter SEO meta description"
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-yellow-400"
              rows={3}
              value={bookData.metaDescription}
              onChange={(e) =>
                setBookData({ ...bookData, metaDescription: e.target.value })
              }
            />
          </div>

          {/* Meta Keywords */}
          <div>
            <label className="block text-yellow-800 font-medium mb-1">
              Meta Keywords (comma separated)
            </label>
            <input
              placeholder="Enter SEO keywords"
              className="w-full border p-3 rounded-lg shadow-sm focus:ring-2 focus:ring-yellow-400"
              value={bookData.metaKeywords}
              onChange={(e) =>
                setBookData({ ...bookData, metaKeywords: e.target.value })
              }
            />
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="space-y-4">
          <button
            onClick={createBook}
            disabled={isLoading}
            className="w-full bg-green-600 text-white text-lg font-semibold py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-all duration-200"
          >
            {isLoading ? "Creating Book..." : "Create Book"}
          </button>

          {isBookCreated && (
            <button
              onClick={goToAddChapter}
              className="w-full bg-indigo-600 text-white text-lg font-semibold py-3 rounded-lg hover:bg-indigo-700 transition-all duration-200"
            >
              ➕ Add Chapter
            </button>
          )}
        </div>
      </div>

      <ToastContainer position="top-center" />
    </div>
  );
}

export default CreateBookPage;
